<template>
  <div class="estrut-module-layout">
    <div v-if="loading" class="estrut-module-layout__loading">
      <div class="estrut-module-layout__spinner" />
      <p>{{ t('estruturacao.lessons.loading') }}</p>
    </div>

    <div v-else-if="error" class="estrut-module-layout__error">
      <p>{{ error }}</p>
      <button type="button" class="estrut-module-layout__retry" @click="loadTree">
        {{ t('estruturacao.lessons.retry') }}
      </button>
    </div>

    <template v-else>
      <div class="estrut-module-layout__grid">
        <EstruturacaoModuleSidebar
          :section="section"
          :tree="tree"
          :selected-aula-id="selectedAulaId"
          :edit-mode="editMode"
          :is-super-admin="isSuperAdmin"
          @select-aula="selectAula"
          @toggle-edit="editMode = !editMode"
          @refresh="loadTree"
        />

        <main class="estrut-module-layout__main">
          <div v-if="!selectedAulaId" class="estrut-module-layout__empty">
            <p>{{ t('estruturacao.lessons.selectLesson') }}</p>
          </div>

          <template v-else-if="aulaLocked">
            <div class="estrut-module-layout__locked">
              <svg class="estrut-module-layout__lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p>{{ t('estruturacao.lessons.lessonLocked') }}</p>
            </div>
          </template>

          <template v-else-if="aulaDetail">
            <EstruturacaoLessonPlayer
              v-if="hasLessonVideo"
              :key="selectedAulaId"
              :aula="aulaDetail"
              @progress="onProgress"
              @completed="onCompleted"
            />

            <EstruturacaoLessonMaterials
              :materiais="aulaDetail.materiais || []"
              :aula-id="selectedAulaId"
              :edit-mode="editMode && isSuperAdmin"
              @refresh="reloadAula"
            />

            <EstruturacaoLessonComments
              :aula-id="selectedAulaId"
            />
          </template>

          <div v-else-if="aulaLoading" class="estrut-module-layout__loading estrut-module-layout__loading--inline">
            <div class="estrut-module-layout__spinner" />
          </div>
        </main>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth'
import { fetchModulo, fetchModuloAdmin, fetchAula, saveProgresso } from '../../services/estruturacaoApi'
import { loadYoutubeIframeApi } from '../../utils/youtubeIframeApi'
import { isValidLessonVideoUrl } from '../../utils/lessonVideoLink'
import EstruturacaoModuleSidebar from './EstruturacaoModuleSidebar.vue'
import EstruturacaoLessonPlayer from './EstruturacaoLessonPlayer.vue'
import EstruturacaoLessonMaterials from './EstruturacaoLessonMaterials.vue'
import EstruturacaoLessonComments from './EstruturacaoLessonComments.vue'

const props = defineProps({
  section: {
    type: Object,
    required: true
  }
})

const { t } = useI18n()
const authStore = useAuthStore()

const modulo = computed(() => props.section.screenId)
const isSuperAdmin = computed(() => authStore.user?.role?.nome === 'SUPER_ADMIN')

const loading = ref(true)
const error = ref('')
const tree = ref(null)
const selectedAulaId = ref(null)
const aulaDetail = ref(null)
const aulaLoading = ref(false)
const editMode = ref(false)

const aulaLocked = computed(() => {
  if (isSuperAdmin.value) return false
  if (!selectedAulaId.value || !tree.value) return false
  for (const cat of tree.value.categorias || []) {
    const aula = (cat.aulas || []).find((a) => a.id === selectedAulaId.value)
    if (aula) return !aula.liberada
  }
  return false
})

const hasLessonVideo = computed(() =>
  aulaDetail.value ? isValidLessonVideoUrl(aulaDetail.value.youtubeUrl) : false
)

async function loadTree() {
  loading.value = true
  error.value = ''
  try {
    const fetcher = isSuperAdmin.value ? fetchModuloAdmin : fetchModulo
    const { data } = await fetcher(modulo.value)
    tree.value = data
    if (!selectedAulaId.value) {
      const first = findFirstUnlocked(data)
      if (first) selectedAulaId.value = first
    } else {
      syncTreeProgress()
    }
  } catch (e) {
    error.value = e.response?.data?.error || t('estruturacao.lessons.loadError')
  } finally {
    loading.value = false
  }
}

function findFirstUnlocked(data) {
  if (isSuperAdmin.value) {
    for (const cat of data.categorias || []) {
      const first = (cat.aulas || [])[0]
      if (first) return first.id
    }
    return null
  }
  for (const cat of data.categorias || []) {
    for (const aula of cat.aulas || []) {
      if (aula.liberada) return aula.id
    }
  }
  return null
}

async function selectAula(aulaId) {
  selectedAulaId.value = aulaId
}

async function reloadAula() {
  if (!selectedAulaId.value) return
  await loadAulaDetail(selectedAulaId.value)
}

async function loadAulaDetail(aulaId) {
  if (aulaLocked.value) {
    aulaDetail.value = null
    return
  }
  aulaLoading.value = true
  try {
    const { data } = await fetchAula(aulaId)
    aulaDetail.value = data
    await maybeAutoCompleteWithoutVideo(data)
  } catch (e) {
    if (e.response?.status === 403) {
      aulaDetail.value = null
    }
  } finally {
    aulaLoading.value = false
  }
}

async function maybeAutoCompleteWithoutVideo(aula) {
  if (!aula || aula.concluida || isValidLessonVideoUrl(aula.youtubeUrl)) return
  try {
    const { data } = await saveProgresso(aula.id, { posicaoSegundos: 0, concluida: true })
    aulaDetail.value = data
    onCompleted({ posicaoSegundos: 0 })
  } catch (e) {
    console.warn('[EstruturacaoLayout] Erro ao auto-concluir aula sem vídeo', e)
  }
}

function updateAulaInTree(aulaId, patch) {
  if (!tree.value?.categorias) return
  for (const cat of tree.value.categorias) {
    const idx = (cat.aulas || []).findIndex((a) => a.id === aulaId)
    if (idx >= 0) {
      cat.aulas[idx] = { ...cat.aulas[idx], ...patch }
      unlockNextIfNeeded(aulaId)
      break
    }
  }
}

function unlockNextIfNeeded(completedId) {
  const flat = []
  for (const cat of tree.value.categorias || []) {
    for (const aula of cat.aulas || []) flat.push(aula)
  }
  const idx = flat.findIndex((a) => a.id === completedId)
  if (idx >= 0 && idx + 1 < flat.length && flat[idx].concluida) {
    flat[idx + 1].liberada = true
  }
}

function syncTreeProgress() {
  if (!aulaDetail.value) return
  updateAulaInTree(aulaDetail.value.id, {
    concluida: aulaDetail.value.concluida,
    posicaoSegundos: aulaDetail.value.posicaoSegundos
  })
}

function onProgress(payload) {
  if (!selectedAulaId.value) return
  updateAulaInTree(selectedAulaId.value, { posicaoSegundos: payload.posicaoSegundos })
  if (aulaDetail.value) {
    aulaDetail.value.posicaoSegundos = payload.posicaoSegundos
  }
}

function onCompleted(payload) {
  if (!selectedAulaId.value) return
  updateAulaInTree(selectedAulaId.value, {
    concluida: true,
    posicaoSegundos: payload.posicaoSegundos ?? aulaDetail.value?.posicaoSegundos
  })
  if (aulaDetail.value) {
    aulaDetail.value.concluida = true
  }
  unlockNextIfNeeded(selectedAulaId.value)
}

watch(selectedAulaId, (id) => {
  if (id) loadAulaDetail(id)
  else aulaDetail.value = null
})

onMounted(async () => {
  await loadYoutubeIframeApi()
  await loadTree()
})
</script>

<style scoped>
.estrut-module-layout {
  min-height: 400px;
}

.estrut-module-layout__grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

@media (min-width: 1024px) {
  .estrut-module-layout__grid {
    flex-direction: row;
    align-items: flex-start;
    gap: 24px;
  }

  .estrut-module-layout__grid > :first-child {
    flex: 0 0 30%;
    max-width: 360px;
  }

  .estrut-module-layout__main {
    flex: 1;
    min-width: 0;
  }
}

.estrut-module-layout__main {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.estrut-module-layout__loading,
.estrut-module-layout__error,
.estrut-module-layout__empty,
.estrut-module-layout__locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
  color: var(--text-secondary);
}

.estrut-module-layout__loading--inline {
  padding: 24px;
}

.estrut-module-layout__spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top-color: var(--accent-primary);
  border-radius: 50%;
  animation: estrut-spin 0.8s linear infinite;
}

@keyframes estrut-spin {
  to { transform: rotate(360deg); }
}

.estrut-module-layout__retry {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
}

.estrut-module-layout__lock-icon {
  width: 48px;
  height: 48px;
  color: var(--text-tertiary);
}

.estrut-module-layout__locked p {
  margin: 0;
  font-size: 1rem;
}
</style>
